import { useState } from 'react';
import type { ReturnDisposition, ReturnDto, ReturnRejectionReason } from '@ecom/contracts';
import { useReturnsStore } from '@/stores/returns.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { count } from '@/lib/format';
import { REJECTION_REASONS_LIST, RETURN_DISPOSITIONS, dispositionKey, rejectionReasonKey } from '@/lib/returns';
import { Choices, ToggleField } from '@/components/fields';
import { Sheet } from '@/components/page';
import { Alert, Button, Field, Textarea } from '@/components/ui';

export type HandlingStep = 'approve' | 'reject' | 'receive';

const COPY: Record<HandlingStep, { title: MessageKey; body: MessageKey; action: MessageKey }> = {
  approve: { title: 'approveTitle', body: 'approveBody', action: 'approve' },
  reject: { title: 'rejectTitle', body: 'rejectBody', action: 'reject' },
  receive: { title: 'receiveTitle', body: 'receiveBody', action: 'receive' },
};

/**
 * Approving, rejecting or receiving one return — the steps that move goods and
 * decisions, not money (refunds have their own panel).
 *
 * One panel for the three, as the order screen has one for every move: say
 * what this step does, ask what it needs — a reason to reject, what became of
 * the item once received — and take a note for the team. Mounted per opening,
 * so each starts clean. Nothing is preselected: a rejection reason and a
 * disposition feed reporting, and a default nobody chose is a wrong figure.
 */
export function StepSheet({ step, row, onClose }: { step: HandlingStep; row: ReturnDto; onClose: () => void }) {
  const { saving, actionError, approve, reject, receive } = useReturnsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  // The note is one field on the return; starting from what is there lets a
  // step add to it rather than silently replace it.
  const [note, setNote] = useState(row.staffNote ?? '');
  const [reason, setReason] = useState<ReturnRejectionReason | null>(null);
  const [disposition, setDisposition] = useState<ReturnDisposition | null>(null);
  const [restock, setRestock] = useState(true);

  const ready = step === 'reject' ? reason !== null : step === 'receive' ? disposition !== null : true;

  async function submit() {
    if (!ready || saving) return;
    const staffNote = note.trim() || undefined;
    const done =
      step === 'approve'
        ? await approve(row.id, staffNote)
        : step === 'reject' && reason
          ? await reject(row.id, reason, staffNote)
          : step === 'receive' && disposition
            ? // Back on sale only when it is fit to sell — the API refuses any other pairing.
              await receive(row.id, { disposition, restock: disposition === 'RESTOCKED' && restock, staffNote })
            : false;
    if (done) onClose();
  }

  return (
    <Sheet
      open
      title={t(COPY[step].title)}
      subtitle={`${row.number} · ${row.productName}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            form="return-step"
            variant={step === 'reject' ? 'danger' : 'primary'}
            disabled={!ready || saving}
          >
            {saving ? t('saving') : t(COPY[step].action)}
          </Button>
        </>
      }
    >
      <form
        id="return-step"
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <p className="text-sm text-ink-muted">{t(COPY[step].body)}</p>

        {step === 'reject' ? (
          <Choices
            legend={t('rejectReasonLabel')}
            name="rejection-reason"
            options={REJECTION_REASONS_LIST.map((value) => ({ value, label: t(rejectionReasonKey(value)) }))}
            value={reason}
            onChange={setReason}
          />
        ) : null}

        {step === 'receive' ? (
          <>
            <Choices
              legend={t('returnDisposition')}
              hint={t('returnDispositionHint')}
              name="disposition"
              options={RETURN_DISPOSITIONS.map((value) => ({ value, label: t(dispositionKey(value)) }))}
              value={disposition}
              onChange={setDisposition}
            />
            {disposition === 'RESTOCKED' ? (
              <ToggleField
                label={t('restock')}
                hint={`${t('restockUnitsHint', { count: count(row.quantity, locale) })} ${t('restockHint')}`}
                checked={restock}
                onChange={setRestock}
              />
            ) : null}
          </>
        ) : null}

        <Field label={t('staffNote')} htmlFor="return-step-note" hint={t('returnStaffNoteHint')}>
          <Textarea
            id="return-step-note"
            rows={3}
            maxLength={1000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>

        {actionError ? <Alert>{actionError}</Alert> : null}
      </form>
    </Sheet>
  );
}
